import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/Base/Form";
import { Link } from "react-router-dom";
import { Button } from "@/components/Base/Button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/store/useAuth";
import { FieldType, InputFields } from "@/components/Common/form/InputFields";
import { AuthFormHeader } from "@/components/Auth/AuthFormHeader";

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email" }),
  password: z
    .string()
    .min(8, { message: "Password should be of minimum 8 characters length" }),
});

const fields: FieldType<z.infer<typeof formSchema>>[] = [
  {
    name: "email",
    label: "Email",
    type: "email",
    placeholder: "Email",
    required: true,
    autoComplete: "email",
    autoFocus: true,
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    placeholder: "Password",
    required: true,
    autoComplete: "new-password",
  },
];

const initialValues = {
  email: "",
  password: "",
};

export const LoginForm = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { authError, login } = useAuth();

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    return login(values.email, values.password);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex min-h-full items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8 rounded-xl border-2 border-white bg-secondary/10 p-8">
            <AuthFormHeader title="Login to Get Started" />
            <div className="mt-6 flex flex-col justify-center gap-4"></div>

            <InputFields fields={fields} form={form} />

            <div className={cn("flex w-96 pb-4", !authError && "invisible")}>
              <div className="w-128 text-destructive">{authError}</div>
            </div>

            <div className={"flex flex-row justify-between"}>
              <Link to="/resetpassword">
                <Button type={"button"}>Forgot Password</Button>
              </Link>
              <Button variant={"accent"} type={"submit"}>
                Sign In
              </Button>
            </div>
            <div className={"flex flex-row justify-center pt-4"}>
              <p>
                Don&#39;t have an account?{" "}
                <Link to="/register" className={"underline"}>
                  Register
                </Link>{" "}
                to get started.
              </p>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
